'use client';

import { SearchIcon } from '@/components/shared/icons';
import CategoryButton from '@/components/shared/categoryButton';
import FilterLabel from '@/components/shared/FilterLabel';

export function BlogFilters({
    searchValue,
    onSearchChange,
    contributors = [],
    activeContributors = [],
    onContributorChange,
    categories = [],
    activeCategories = [],
    onCategoryChange,
    sortOrder,
    onSortChange,
    onClear
}) {
    const hasActiveFilters =
        activeContributors.length > 0 ||
        activeCategories.length > 0 ||
        searchValue.trim().length > 0 ||
        sortOrder !== 'desc';

    const contributorOptions = contributors.map((c) => ({
        value: c.identifier || c.inode,
        label: `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim()
    }));

    const selectedContributorOptions = activeContributors
        .map((id) => {
            const c = contributors.find((x) => x.identifier === id || x.inode === id);
            return c
                ? {
                      value: id,
                      label: `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim()
                  }
                : null;
        })
        .filter(Boolean);

    const categoryOptions = categories.map((cat) => ({
        value: cat.key,
        label: cat.categoryName
    }));

    const selectedCategoryOptions = activeCategories
        .map((key) => {
            const cat = categories.find((c) => c.key === key);
            return cat ? { value: key, label: cat.categoryName } : null;
        })
        .filter(Boolean);

    return (
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:gap-4">
            <div className="flex flex-1 flex-col">
                <FilterLabel>Search</FilterLabel>
                <div className="relative flex h-[42px] items-center">
                    <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="block size-full rounded-lg bg-blue-100 p-3 ps-11 text-sm text-blue-600 outline-none placeholder:text-blue-600"
                        placeholder="Search posts..."
                    />
                </div>
            </div>

            <div className="flex flex-shrink-0 flex-col">
                <FilterLabel>Sort by</FilterLabel>
                <select
                    value={sortOrder}
                    onChange={(e) => onSortChange(e.target.value)}
                    className="h-[42px] min-w-[160px] rounded-lg bg-blue-100 px-4 text-sm font-medium text-blue-700 outline-none">
                    <option value="desc">Newest first</option>
                    <option value="asc">Oldest first</option>
                </select>
            </div>

            <div className="relative z-20 flex w-[220px] flex-shrink-0 flex-col">
                <FilterLabel>Authors</FilterLabel>
                <CategoryButton
                    multiselect
                    enableSmartSearch
                    searchPlaceholder="Search authors..."
                    options={contributorOptions}
                    selectedOptions={selectedContributorOptions}
                    onSelectionChange={(selected) => {
                        onContributorChange(selected.map((s) => s.value));
                    }}
                    defaultText="All authors"
                    maxDisplayItems={2}
                    className="flex w-full items-center justify-center gap-2 bg-fuschia-700 px-3 text-base text-white">
                    All authors
                </CategoryButton>
            </div>

            <div className="relative z-10 flex w-[220px] flex-shrink-0 flex-col">
                <FilterLabel>Topics</FilterLabel>
                <CategoryButton
                    multiselect
                    enableSmartSearch
                    searchPlaceholder="Search topics..."
                    options={categoryOptions}
                    selectedOptions={selectedCategoryOptions}
                    onSelectionChange={(selected) => {
                        onCategoryChange(selected.map((s) => s.value));
                    }}
                    defaultText="All topics"
                    maxDisplayItems={2}
                    className="flex w-full items-center justify-center gap-2 bg-fuschia-700 px-3 text-base text-white">
                    All topics
                </CategoryButton>
            </div>

            <div
                className={`flex items-end overflow-hidden transition-all duration-300 ease-in-out ${hasActiveFilters ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'}`}>
                <button
                    onClick={onClear}
                    className="h-[42px] whitespace-nowrap rounded-lg px-6 text-base font-semibold text-blue-600 hover:bg-fuschia-200 hover:text-black">
                    Clear
                </button>
            </div>
        </div>
    );
}

export default BlogFilters;
